import { computed, makeObservable, observable, action, runInAction } from 'mobx';
import { Common } from 'src/AppState/Common';
import { DateTime } from 'src_common/utils/time/time';
import { ComplianceFeatureFilterType, ComplianceGamstopRequestLogType } from 'src/api_openapi/v2/compliance/schemas';
import { apiComplianceUserAuditRequest } from 'src/api_openapi/v2/compliance/apiComplianceUserAudit';
import { apiGetGamstopRequestHistoryRequest } from 'src/api_openapi/v2/compliance/apiGetGamstopRequestHistory';

const PAGE_SIZE = 100;

export type AuditLogRowType = {
    id: number;
    at: string;
    op: string;
    actor_id: string;
    entrypoint?: string;
    reason?: string;
    before?: unknown;
    after?: unknown;
    cols_changed?: string[];
};

const mapGamstopRequestToAuditLog = (row: ComplianceGamstopRequestLogType): AuditLogRowType => ({
    id: row.id,
    at: row.at,
    actor_id: row.actor_id,
    entrypoint: row.entrypoint == null ? undefined : row.entrypoint,
    reason: row.reason == null ? undefined : row.reason,
    op: row.op,
    after: row.data ?? undefined,
});

export class ComplianceAuditStore {
    @observable public rows: Array<AuditLogRowType> = [];
    @observable public status: 'loading' | 'error' | 'ready' = 'loading';
    @observable public isLoadingMore = false;
    @observable public hasMore = false;

    @observable public from: string | undefined;
    @observable public to: string | undefined;

    public constructor(
        private readonly common: Common,
        private readonly accountId: number,
        private readonly feature: ComplianceFeatureFilterType
    ) {
        this.from = DateTime.current().subtractDays(30).format('YYYY-MM-DDT00:00:00');
        this.to = DateTime.current().format('YYYY-MM-DDT23:59:59');

        makeObservable(this);
        void this.fetchLogs();
    }

    private fetchAuditPage = async (lastId?: number): Promise<Array<AuditLogRowType> | null> => {
        const dateRange = {
            from: `${this.from}Z`,
            ...(this.to === undefined ? {} : { to: `${this.to}Z` }),
        };
        const authorization = { authorization: `Bearer ${this.common.session.currentJwt}` };

        if (this.feature === 'Gamstop') {
            const response = await apiGetGamstopRequestHistoryRequest(
                this.common.appEnvParams.gatewayUrl,
                {
                    requestBody: {
                        accountId: this.accountId,
                        limit: PAGE_SIZE,
                        ...(lastId === undefined ? {} : { lastId }),
                        dateRange,
                    },
                },
                authorization
            );

            if (response.status === 200) {
                return response.body.rows.map(mapGamstopRequestToAuditLog);
            }

            return null;
        }

        const response = await apiComplianceUserAuditRequest(
            this.common.appEnvParams.gatewayUrl,
            {
                requestBody: {
                    feature: this.feature,
                    accountId: this.accountId,
                    limit: PAGE_SIZE,
                    ...(lastId === undefined ? {} : { lastId }),
                    dateRange,
                },
            },
            authorization
        );

        if (response.status === 200) {
            return response.body.rows;
        }

        return null;
    };

    @action private fetchLogs = async (): Promise<void> => {
        this.status = 'loading';
        this.rows = [];
        this.hasMore = false;

        try {
            const result = await this.fetchAuditPage();

            runInAction(() => {
                if (result === null) {
                    this.status = 'error';
                    return;
                }

                this.rows = result;
                this.hasMore = result.length === PAGE_SIZE;
                this.status = 'ready';
            });
        } catch {
            runInAction(() => {
                this.status = 'error';
            });
        }
    };

    @action public loadMore = async (): Promise<void> => {
        if (!this.hasMore || this.isLoadingMore) {
            return;
        }

        const lastId = this.rows[this.rows.length - 1]?.id;
        if (lastId === undefined) {
            return;
        }

        this.isLoadingMore = true;

        try {
            const result = await this.fetchAuditPage(lastId);

            runInAction(() => {
                this.isLoadingMore = false;

                if (result === null) {
                    return;
                }

                this.rows = [...this.rows, ...result];
                this.hasMore = result.length === PAGE_SIZE;
            });
        } catch {
            runInAction(() => {
                this.isLoadingMore = false;
            });
        }
    };

    private createDateHandler = (field: 'from' | 'to'): ((date: string | null) => Promise<void>) => {
        const handler = async (date: string | null): Promise<void> => {
            if (date === null || date === '') {
                this[field] = undefined;
            } else {
                this[field] = DateTime.from(date)?.format('YYYY-MM-DDTHH:mm:ss');
            }

            await this.fetchLogs();
        };
        return action(handler);
    };

    public handleChangeFromDate = this.createDateHandler('from');
    public handleChangeToDate = this.createDateHandler('to');

    @computed public get auditLogs(): Array<AuditLogRowType> | 'loading' | 'error' {
        if (this.status === 'loading' && this.rows.length === 0) {
            return 'loading';
        }

        if (this.status === 'error' && this.rows.length === 0) {
            return 'error';
        }

        return this.rows;
    }
}
